import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  DollarSign, 
  TrendingUp, 
  Home, 
  Wrench, 
  FileText, 
  Bell,
  Calendar,
  CheckCircle2,
  Clock
} from "lucide-react";
import { motion } from "framer-motion";

export const DashboardPreview = () => {
  const ownerFeatures = [
    { icon: DollarSign, label: "Real-Time Rent Tracking", description: "See payments the moment they're received" },
    { icon: FileText, label: "Detailed Financial Reports", description: "Monthly statements, 1099s, expense tracking" },
    { icon: Wrench, label: "Maintenance Management", description: "Track repairs, approve expenses, view photos" },
    { icon: Home, label: "Property Performance", description: "Occupancy rates, ROI analysis, market insights" },
    { icon: Calendar, label: "Lease Management", description: "Renewal tracking, document storage, e-signatures" },
    { icon: Bell, label: "Instant Notifications", description: "Get alerts for payments, maintenance, and issues" }
  ];

  const tenantFeatures = [
    { icon: DollarSign, label: "Online Rent Payment", description: "Pay rent from any device - bank account or card" },
    { icon: Wrench, label: "Maintenance Requests", description: "Submit requests 24/7 with photos and priority level" },
    { icon: FileText, label: "Digital Lease Access", description: "View lease, addendums, and property documents" },
    { icon: Bell, label: "Automated Reminders", description: "Never miss rent due dates or important notices" }
  ];

  const recentActivity = [
    { type: "payment", tenant: "123 Oak St", amount: "$1,850", status: "Received", time: "2 hours ago", icon: CheckCircle2, color: "text-green-600" },
    { type: "maintenance", property: "456 Maple Ave", title: "HVAC Filter Replacement", status: "Completed", time: "5 hours ago", icon: Wrench, color: "text-blue-600" },
    { type: "payment", tenant: "789 Pine Ln", amount: "$2,100", status: "Received", time: "1 day ago", icon: CheckCircle2, color: "text-green-600" },
    { type: "maintenance", property: "321 Elm St", title: "Plumbing Repair", status: "In Progress", time: "2 days ago", icon: Clock, color: "text-orange-600" }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
            Your Properties, <span className="text-primary">Completely Managed</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Our powerful property management system gives you 24/7 access to everything - financials, maintenance, tenants, and more. All from your phone, tablet, or computer.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Tabs defaultValue="owner" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 h-auto">
              <TabsTrigger value="owner" className="text-base py-3" data-testid="dashboard-preview-tab">
                <Home className="h-5 w-5 mr-2" />
                For Property Owners
              </TabsTrigger>
              <TabsTrigger value="tenant" className="text-base py-3" data-testid="dashboard-preview-tab">
                <Bell className="h-5 w-5 mr-2" />
                For Tenants
              </TabsTrigger>
            </TabsList>

            {/* OWNER PORTAL */}
            <TabsContent value="owner" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Dashboard */}
                <div className="lg:col-span-2 space-y-6">
                  <Card className="shadow-xl">
                    <CardHeader>
                      <CardTitle className="font-display text-2xl">Portfolio Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-4 bg-primary/5 rounded-lg">
                          <div className="text-3xl font-bold font-display text-primary">$12,450</div>
                          <div className="text-sm text-muted-foreground mt-1">This Month</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-3xl font-bold font-display text-green-600">98%</div>
                          <div className="text-sm text-muted-foreground mt-1">Occupied</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                          <div className="text-3xl font-bold font-display text-secondary">3</div>
                          <div className="text-sm text-muted-foreground mt-1">Open Requests</div>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-3xl font-bold font-display text-blue-600">6</div>
                          <div className="text-sm text-muted-foreground mt-1">Properties</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium">Rent Collection Progress</span>
                            <span className="text-primary font-semibold">$12,450 / $12,700</span>
                          </div>
                          <Progress value={98} className="h-3" />
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium">Portfolio Occupancy</span>
                            <span className="text-green-600 font-semibold">98%</span>
                          </div>
                          <Progress value={98} className="h-3 [&>div]:bg-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Activity */}
                  <Card className="shadow-xl">
                    <CardHeader>
                      <CardTitle className="font-display">Recent Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {recentActivity.map((activity, index) => {
                          const Icon = activity.icon;
                          return (
                            <div key={index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                              <Icon className={`h-5 w-5 mt-0.5 ${activity.color}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="font-semibold text-sm">{activity.tenant || activity.property}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {activity.amount || activity.title}
                                    </p>
                                  </div>
                                  <Badge variant="outline" className="text-xs">{activity.status}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Features Sidebar */}
                <div className="space-y-6">
                  <Card className="shadow-xl">
                    <CardHeader>
                      <CardTitle className="font-display text-lg">Owner Portal Features</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {ownerFeatures.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                          <div key={index} className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm mb-1">{feature.label}</h4>
                              <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* TENANT PORTAL */}
            <TabsContent value="tenant" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="shadow-xl">
                  <CardHeader>
                    <CardTitle className="font-display">Tenant Dashboard</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="p-4 bg-primary/5 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Next Rent Payment</span>
                        <Badge>Due in 5 days</Badge>
                      </div>
                      <div className="text-3xl font-bold font-display text-primary mb-3">$1,850</div>
                      <Button className="w-full bg-secondary hover:bg-secondary/90">Pay Now</Button>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm">Your Active Requests</h4>
                      <div className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">Kitchen Sink Leak</span>
                          <Badge variant="outline" className="bg-orange-50">In Progress</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">Plumber scheduled for tomorrow 10am-12pm</p>
                      </div>
                      <Button variant="outline" className="w-full">Submit New Request</Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-xl">
                  <CardHeader>
                    <CardTitle className="font-display text-lg">Tenant Portal Features</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {tenantFeatures.map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm mb-1">{feature.label}</h4>
                            <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          <div className="text-center mt-10">
            <p className="text-muted-foreground mb-4 text-xl font-medium">
              Experience the difference of professional property management software
            </p>
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-lg px-8 py-6 h-auto" asChild>
              <a href="#contact">Request a Demo</a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DashboardPreview;
